import React, { useState } from "react";
import { Company } from "../../types/company.types";
import CompanyLinkForm from "../CompanyLinks/CompanyLinkForm";
import { Button, Dialog } from "../ui";
import { cn } from "../ui/cn";
import { t } from "../../i18n";

interface CompanySelectDialogProps {
  companies: Company[];
  onConfirm: (selected: Company[]) => void;
  onClose: () => void;
}

// O PDF só comporta 3 empresas por pedido.
const MAX_SELECTABLE_COMPANIES = 3;

type Step = "ask" | "link" | "select";

const TITLES: Record<Step, string> = {
  ask: t("companySelect.titleAsk"),
  link: t("companySelect.titleLink"),
  select: t("companySelect.titleSelect"),
};

const companySubtitle = (company: Company) => [company.cnpj, company.phone, company.email].filter(Boolean).join(" · ");

export const CompanySelectDialog: React.FC<CompanySelectDialogProps> = ({ companies, onConfirm, onClose }) => {
  const [step, setStep] = useState<Step>("ask");
  const [checkedIds, setCheckedIds] = useState<Set<number>>(new Set());

  // Empresas vinculadas durante a própria etapa "link" (pode acontecer mais
  // de uma vez na mesma sessão do modal) — mantidas à parte da prop
  // `companies` porque não há garantia de que o componente-pai já
  // re-renderizou com a lista atualizada (o vínculo sincroniza via
  // `SessionContext`, que se propaga de forma assíncrona) no exato instante em
  // que avançamos para a etapa de seleção.
  const [justLinkedCompanies, setJustLinkedCompanies] = useState<Company[]>([]);

  const availableCompanies = [
    ...companies,
    ...justLinkedCompanies.filter((linked) => !companies.some((c) => c.id === linked.id)),
  ];

  const toggleCompany = (id: number) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      // O checkbox já fica `disabled` no limite; isto é só a segunda barreira.
      else if (next.size < MAX_SELECTABLE_COMPANIES) next.add(id);
      return next;
    });
  };

  // Sem empresas vinculadas não há o que listar: leva ao mesmo fluxo de
  // vínculo de Configurações, sem sair do pedido.
  const handleWantsToChoose = () => setStep(availableCompanies.length > 0 ? "select" : "link");

  // A empresa recém-vinculada entra marcada, sem perder as marcações anteriores.
  const handleCompanyLinked = (company: Company) => {
    setJustLinkedCompanies((prev) => (prev.some((c) => c.id === company.id) ? prev : [...prev, company]));
    setCheckedIds((prev) => (prev.size >= MAX_SELECTABLE_COMPANIES ? prev : new Set(prev).add(company.id)));
    setStep("select");
  };

  const back = <Button variant="outline" fullWidth onClick={() => setStep("ask")}>{t("companySelect.back")}</Button>;
  const footer: Record<Step, React.ReactNode> = {
    ask: (
      <>
        <Button variant="outline" fullWidth onClick={() => onConfirm([])}>{t("companySelect.no")}</Button>
        <Button variant="primary" fullWidth onClick={handleWantsToChoose}>{t("companySelect.yes")}</Button>
      </>
    ),
    link: back,
    select: (
      <>
        {back}
        <Button variant="primary" fullWidth onClick={() => onConfirm(availableCompanies.filter((c) => checkedIds.has(c.id)))}>
          {t("companySelect.confirm")}
        </Button>
      </>
    ),
  };

  return (
    <Dialog
      open
      onClose={onClose}
      size="sm"
      title={
        <span className="flex items-center gap-2">
          {TITLES[step]}
          {step === "select" && (
            <span className="text-caption font-semibold text-fg-muted">
              {t("companySelect.count", { count: checkedIds.size, max: MAX_SELECTABLE_COMPANIES })}
            </span>
          )}
        </span>
      }
      footer={<div className="flex w-full gap-2">{footer[step]}</div>}
    >
      {step === "ask" && <p className="text-body text-fg-muted">{t("companySelect.ask")}</p>}

      {step === "link" && (
        <>
          <p className="mb-4 text-caption text-fg-muted">
            {availableCompanies.length > 0 ? t("companySelect.linkMore") : t("companySelect.linkFirst")}
          </p>
          <CompanyLinkForm existingCompanies={availableCompanies} onLinked={handleCompanyLinked} submitLabel={t("companySelect.linkSubmit")} />
        </>
      )}

      {step === "select" && (
        <>
          <div className="mb-3 flex items-center justify-between gap-2">
            <p className="text-caption text-fg-muted">{t("companySelect.selectHint", { max: MAX_SELECTABLE_COMPANIES })}</p>
            <Button variant="text" size="sm" onClick={() => setStep("link")}>{t("companySelect.linkAnother")}</Button>
          </div>
          <ul className="flex flex-col gap-2">
            {availableCompanies.map((company) => {
              const subtitle = companySubtitle(company);
              const checked = checkedIds.has(company.id);
              const limitReached = !checked && checkedIds.size >= MAX_SELECTABLE_COMPANIES;
              return (
                <li key={company.id}>
                  <label
                    title={limitReached ? t("companySelect.limitReached", { max: MAX_SELECTABLE_COMPANIES }) : undefined}
                    className={cn(
                      "flex min-h-control-touch items-center gap-3 rounded-control border border-line bg-surface-muted px-3 py-2.5 transition-colors duration-fast",
                      limitReached ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:bg-surface-hover"
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={limitReached}
                      onChange={() => toggleCompany(company.id)}
                      className="h-4 w-4 shrink-0 accent-[rgb(var(--primary))] disabled:cursor-not-allowed"
                    />
                    <span className="min-w-0">
                      <span className="block truncate text-body font-medium text-fg">{company.name}</span>
                      {subtitle && <span className="block truncate text-caption text-fg-subtle">{subtitle}</span>}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </Dialog>
  );
};
